import { ApiClient } from './api';

/**
 * Twilio can prove the browser hung up (`sdk_edge.disconnected_by = "local"`) but never
 * why — a deliberate `Device.disconnectAll()` is indistinguishable from a clean hangup
 * from outside this process. So each end is stamped with a cause here and posted to the
 * server to be logged; `EventLogger` does not reach New Relic.
 *
 * Shared by both softphones so a call ending on either UI produces a comparable row.
 */

export type EndReason = 'hangup' | 'disconnect' | 'cancel' | 'error';

/**
 * The server classifies anything outside `user-hangup` / `remote-hangup` /
 * `call-cancel` / `dial-failed` as an unexpected teardown, so those four spellings are
 * a wire contract — do not re-hyphenate them. `device-offline` is a voice-sdk 1.x event
 * name that should never fire on 2.x; no rows for it is expected, not a broken reporter.
 */
export type CallEndCause =
	| 'user-hangup'
	| 'pipeline-teardown'
	| 'legacy-teardown'
	| 'device-evicted'
	| 'device-offline'
	| 'device-error'
	| 'remote-hangup'
	| 'call-error'
	| 'call-cancel'
	| 'websocket-hangup'
	| 'dial-failed';

/** Structural rather than the SDK's `Call` so this module, and its tests, stay SDK-free. */
type ReportableCall = {
	parameters: Record<string, string>;
	customParameters: Map<string, string>;
	status: () => string;
};

type CallDirection = 'inbound' | 'outbound';

type ReportOptions = {
	errorCode?: number;
	deviceState?: string;
};

const TAB_ID_KEY = 'levitate.aida.tabId';

/** Twilio's `Call.State.Open`, inlined to keep the voice SDK out of this module. */
const OPEN_STATUS = 'open';

const fallbackTabId = `tab-${Math.random().toString(36).slice(2, 10)}`;

/**
 * Two distinct ids against one `phoneCallId` prove more than one tab drove the call.
 * Shared with the recording-playback report so both rows carry the same tab identity.
 */
export function getTabId(): string {
	try {
		const existing = window.sessionStorage.getItem(TAB_ID_KEY);
		if (existing) return existing;
		window.sessionStorage.setItem(TAB_ID_KEY, fallbackTabId);
		return fallbackTabId;
	} catch {
		return fallbackTabId;
	}
}

/**
 * Per-Device bookkeeping behind the call-end diagnostic. Owned by one softphone and
 * shared across its teardown paths, which deliberately overlap: a controller reports
 * before `disconnectAll()` so the cause survives `destroy()`, and the `disconnect` that
 * follows would otherwise report the same end again.
 */
export class CallEndReporter {
	private readonly apiClient: ApiClient;
	private readonly reported = new WeakSet<ReportableCall>();
	/**
	 * Twilio sets the status to `closed` *before* it emits `disconnect`/`cancel`, so a
	 * handler reading `status()` can never tell the rep was mid-call — which is exactly
	 * the case this diagnostic exists to find. Tracked off `accept` instead.
	 */
	private readonly wentLive = new WeakSet<ReportableCall>();
	/**
	 * Keyed per call, not a single field: per-call listeners are never detached, so a
	 * superseded Call reporting late would otherwise take the newer call's direction.
	 */
	private readonly directions = new WeakMap<ReportableCall, CallDirection>();
	private pendingCause: CallEndCause | undefined;

	constructor(apiClient: ApiClient) {
		this.apiClient = apiClient;
	}

	/** Also drops a cause stamped for a teardown that never completed, which would mislabel this call. */
	public trackCall(call: ReportableCall, direction: CallDirection): void {
		this.directions.set(call, direction);
		this.pendingCause = undefined;
	}

	public markLive(call: ReportableCall): void {
		this.wentLive.add(call);
	}

	public stampCause(cause: CallEndCause): void {
		this.pendingCause = cause;
	}

	public stampCauseIfUnset(cause: CallEndCause): void {
		this.pendingCause = this.pendingCause ?? cause;
	}

	public takeCause(fallback: CallEndCause): CallEndCause {
		const cause = this.pendingCause ?? fallback;
		this.pendingCause = undefined;
		return cause;
	}

	public report(call: ReportableCall, cause: CallEndCause, reason: EndReason, options: ReportOptions = {}): void {
		if (this.reported.has(call)) return;
		this.reported.add(call);

		// The endpoint is keyed by it, and without it there is nothing to correlate to.
		const phoneCallId = call.customParameters.get('phoneCallId');
		if (!phoneCallId) return;

		let callStatus: string | undefined;
		try {
			callStatus = call.status();
		} catch {
			callStatus = undefined;
		}

		try {
			void this.apiClient
				.reportClientCallEnd(phoneCallId, {
					callSid: call.parameters.CallSid ?? undefined,
					callStatus,
					cause,
					deviceState: options.deviceState,
					direction: this.directions.get(call),
					errorCode: options.errorCode,
					hadActiveCall: this.wentLive.has(call) || callStatus === OPEN_STATUS,
					reason,
					tabId: getTabId(),
					tabVisibility: typeof document === 'undefined' ? undefined : document.visibilityState,
				})
				.catch(() => {
					/* diagnostics are best-effort */
				});
		} catch {
			/* diagnostics are best-effort */
		}
	}
}
